import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as api from '../../api/AccountsAPI/verifyTLPaymentAPI';

const err = (e, fallback) => e?.ErrorDetails?.ErrorMessage || e?.Message || e?.message || fallback;

export const fetchVerifyTLPaymentQueue = createAsyncThunk(
    'verifyTLPayment/fetchQueue',
    async (roleId, { rejectWithValue }) => {
        try { return await api.getVerificationTLPayments(roleId); }
        catch (e) { return rejectWithValue(err(e, 'Failed to fetch term loan payment verification queue')); }
    }
);

export const fetchVerifyTLPaymentDetail = createAsyncThunk(
    'verifyTLPayment/fetchDetail',
    async (rowId, { rejectWithValue }) => {
        try { return await api.getTLPaymentById(rowId); }
        catch (e) { return rejectWithValue(err(e, 'Failed to fetch term loan payment details')); }
    }
);

export const submitVerifyTLPayment = createAsyncThunk(
    'verifyTLPayment/verify',
    async (payload, { rejectWithValue }) => {
        try { return await api.verifyTLPayment(payload); }
        catch (e) { return rejectWithValue(err(e, 'Error Occurred While Verification')); }
    }
);

const initialState = {
    queue: [], queueLoading: false, queueError: null,
    detail: null, detailKey: null, detailLoading: false, detailError: null,
    verifyLoading: false,
};

const firstRow = (data) => (Array.isArray(data) ? data[0] || null : data || null);

const verifyTLPaymentSlice = createSlice({
    name: 'verifyTLPayment',
    initialState,
    reducers: {
        clearVerifyTLPaymentDetail: (s) => { s.detail = null; s.detailKey = null; s.detailError = null; s.detailLoading = false; },
        resetVerifyTLPayment: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchVerifyTLPaymentQueue.pending, (s) => { s.queueLoading = true; s.queueError = null; })
            .addCase(fetchVerifyTLPaymentQueue.fulfilled, (s, a) => { s.queueLoading = false; s.queue = a.payload?.Data || []; })
            .addCase(fetchVerifyTLPaymentQueue.rejected, (s, a) => { s.queueLoading = false; s.queueError = a.payload; s.queue = []; });

        builder
            .addCase(fetchVerifyTLPaymentDetail.pending, (s) => { s.detailLoading = true; s.detailError = null; s.detail = null; s.detailKey = null; })
            .addCase(fetchVerifyTLPaymentDetail.fulfilled, (s, a) => {
                s.detailLoading = false;
                s.detail = firstRow(a.payload?.Data);
                // The detail row does not echo the payment transaction no it was requested by.
                s.detailKey = a.meta.arg;
            })
            .addCase(fetchVerifyTLPaymentDetail.rejected, (s, a) => { s.detailLoading = false; s.detailError = a.payload; s.detail = null; });

        builder
            .addCase(submitVerifyTLPayment.pending, (s) => { s.verifyLoading = true; })
            .addCase(submitVerifyTLPayment.fulfilled, (s) => { s.verifyLoading = false; })
            .addCase(submitVerifyTLPayment.rejected, (s) => { s.verifyLoading = false; });
    },
});

export const { clearVerifyTLPaymentDetail, resetVerifyTLPayment } = verifyTLPaymentSlice.actions;

export const selectVTPQueue = (s) => s.verifyTLPayment.queue;
export const selectVTPQueueLoading = (s) => s.verifyTLPayment.queueLoading;
export const selectVTPQueueError = (s) => s.verifyTLPayment.queueError;
export const selectVTPDetail = (s) => s.verifyTLPayment.detail;
export const selectVTPDetailKey = (s) => s.verifyTLPayment.detailKey;
export const selectVTPDetailLoading = (s) => s.verifyTLPayment.detailLoading;
export const selectVTPDetailError = (s) => s.verifyTLPayment.detailError;
export const selectVTPVerifyLoading = (s) => s.verifyTLPayment.verifyLoading;

export default verifyTLPaymentSlice.reducer;
